import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';
import { isEmptyObject } from '@shared/utils/isEmptyObject';

export const DISCOUNT_FIELDS = ['discount_min_amount', 'discount_max_amount'] as const;

export type TDiscountField = (typeof DISCOUNT_FIELDS)[number];
export type TDiscountRangeErrors = Partial<Record<TDiscountField, string>>;

// Mirrors the backend rules: a whole number not less than 1 (deposit amounts have no fractions,
// so decimal limits are rejected with 422), and min <= max with the range error on the min field
const AMOUNT_PATTERN = /^\d+$/;

export const AMOUNT_MESSAGE = 'Must be a whole number greater than 0';
export const RANGE_MESSAGE = 'Must be less than or equal to Discount max amount';

const isValidAmount = (value: string) => AMOUNT_PATTERN.test(value) && Number(value) > 0;

// Empty means "no limit", so only filled values are checked
export const validateDiscountRange = (min: string, max: string): TDiscountRangeErrors => {
  const errors: TDiscountRangeErrors = {};

  if (min && !isValidAmount(min)) errors.discount_min_amount = AMOUNT_MESSAGE;
  if (max && !isValidAmount(max)) errors.discount_max_amount = AMOUNT_MESSAGE;

  if (isEmptyObject(errors) && min && max && Number(min) > Number(max)) {
    errors.discount_min_amount = RANGE_MESSAGE;
  }

  return errors;
};

// Local errors are shaped like a backend 422 so FormItemWithError renders them under the field
export const toValidationError = (errors: TDiscountRangeErrors): FetchBaseQueryError => ({
  status: 422,
  data: {
    errors: Object.fromEntries(Object.entries(errors).map(([field, msg]) => [field, [msg]])),
  },
});
